import { parseUtcInstant } from 'components/CallScribe/Recents/recentsHelpers';

export type TRecordingControlState =
  | 'hidden'
  | 'active'
  | 'stopping'
  | 'stopped'
  | 'failed';

export const RECORDING_ACTIVE_LABEL = 'Recording';

export const STOP_RECORDING_LABEL = 'Stop recording';

export const STOPPING_RECORDING_LABEL = 'Stopping…';

export const RECORDING_STOPPED_LABEL = 'Recording stopped';

export const STOP_FAILED_TEXT = 'The recording could not be stopped. Try again.';

export const STOP_RETRY_LABEL = 'Try again';

export interface IRecordingSignal {
  isRecording?: boolean | null;
  recordingSid?: string | null;
  recordingStoppedAtUtc?: string | null;
  endedAtUtc?: string | null;
}

export interface ILiveRecordingSignal {
  isRecording?: boolean | null;
  recordingStoppedAtUtc?: string | null;
}

export const stoppedAtOf = (
  call: IRecordingSignal | null | undefined,
  live: ILiveRecordingSignal | null | undefined = undefined,
): string | null => call?.recordingStoppedAtUtc ?? live?.recordingStoppedAtUtc ?? null;

export const isRecordingActive = (
  call: IRecordingSignal | null | undefined,
  live: ILiveRecordingSignal | null | undefined = undefined,
): boolean => {
  if (stoppedAtOf(call, live)) {
    return false;
  }
  if (call?.endedAtUtc) {
    return false;
  }
  if (typeof call?.isRecording === 'boolean') {
    return call.isRecording;
  }
  if (typeof live?.isRecording === 'boolean') {
    return live.isRecording;
  }
  return Boolean(call?.recordingSid);
};

export const resolveRecordingState = (
  call: IRecordingSignal | null | undefined,
  local: { stopping?: boolean; failed?: boolean } = {},
  live: ILiveRecordingSignal | null | undefined = undefined,
): TRecordingControlState => {
  if (stoppedAtOf(call, live)) {
    return 'stopped';
  }
  if (!isRecordingActive(call, live)) {
    return 'hidden';
  }
  if (local.failed) {
    return 'failed';
  }
  if (local.stopping) {
    return 'stopping';
  }
  return 'active';
};

export const elapsedClock = (
  atUtc: string | null | undefined,
  answeredAtUtc: string | null | undefined,
): string | null => {
  const at = parseUtcInstant(atUtc);
  const answered = parseUtcInstant(answeredAtUtc);
  if (!at || !answered) {
    return null;
  }
  const seconds = Math.floor((at.getTime() - answered.getTime()) / 1000);
  if (seconds < 0) {
    return null;
  }
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
};

export const recordingStoppedLabel = (
  call: IRecordingSignal & { answeredAtUtc?: string | null },
): string | null => {
  if (!call?.recordingStoppedAtUtc) {
    return null;
  }
  const clock = elapsedClock(call.recordingStoppedAtUtc, call.answeredAtUtc);
  return clock
    ? `Recording stopped by the technician at\u00A0${clock}`
    : 'Recording stopped by the technician';
};
